/**
 * The tentacle's complete pose — base curve, emergence, travelling wave, and grip — as
 * one pure function of simulation time.
 *
 * This exists because the pose is evaluated twice: the renderer bends the mesh with it,
 * and the simulation strings the limb's hit spheres along it. Any drift between the two
 * re-creates the bug this architecture was built to kill — a limb that visibly coils over
 * a hull or the deck edge while its hit volume stays in the free-writhing pose fifteen
 * metres away. Same contract as the wave field and the ocean shader: one description,
 * two consumers, no second opinion.
 *
 * Everything here is local space (the limb's own frame, root at the origin's water
 * column) and driven only by sim time, entity id and vessel state, so captures and
 * replays stay deterministic.
 */

import type { Vessel } from '../sim/types';
import { tentaclePathPoint, type CreaturePoint } from './creatureScale';
import {
  tentacleEmergence,
  tentacleMotionFor,
  tentacleOffset,
  type TentacleMotion,
} from './tentacleMotion';
import {
  krakenTentacleGrip,
  krakenTentacleTarget,
  krakenTentacleWrapPoint,
  type KrakenTentacleGrip,
} from './krakenPredation';
import { krakenRigGrip, krakenRigWrapPoint, type KrakenRigGrip } from './krakenRig';

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
}

/** Everything the pose needs, resolved once per limb per frame/tick. */
export interface TentaclePose {
  motion: TentacleMotion;
  /** 0 under the sea, 1 fully emerged. */
  rise: number;
  /**
   * How far into its reach the limb is, 0..1. A limb that has just surfaced writhes
   * free for a beat before it goes for anything — grip climbing with age is what turns
   * "appeared holding the rig" into "came up and took hold of it".
   */
  gripStrength: number;
  shipGrip: KrakenTentacleGrip | null;
  rigGrip: KrakenRigGrip | null;
}

/**
 * Resolve this limb's pose inputs. `vessels` is needed to find what it is holding.
 */
export function tentaclePose(
  limb: Vessel,
  vessels: readonly Vessel[],
  simTime: number,
): TentaclePose {
  const age = simTime - limb.spawnedAt;
  const rise = tentacleEmergence(age);
  // Reach follows emergence: up first, then out. Instant for limbs that predate the
  // shift clock (benchmarks, tests), because their age is infinite.
  const reach = Number.isFinite(age) ? smoothstep((age - 1.1) / 2.8) : 1;

  const target = krakenTentacleTarget(limb, vessels);
  const shipGrip = target ? krakenTentacleGrip(limb, target, simTime) : null;
  const rigGrip = !target ? krakenRigGrip(limb, simTime) : null;
  const raw = shipGrip?.grip ?? rigGrip?.grip ?? 0;

  return {
    motion: tentacleMotionFor(limb.id),
    rise,
    gripStrength: raw * reach * rise,
    shipGrip,
    rigGrip,
  };
}

/**
 * The deformed centreline at progress `t`, in the limb's local frame.
 *
 * Order matters and is shared law: the authored curve is telescoped by emergence (a limb
 * still rising has not got everywhere yet), the travelling contraction is added on top,
 * and only then is the result bound toward the wrap — root planted, the rest committed
 * to whatever it holds in proportion to its grip.
 */
export function tentacleCentrePoint(
  pose: TentaclePose,
  t: number,
  simTime: number,
): CreaturePoint {
  const clamped = clamp01(t);
  const centre = tentaclePathPoint(clamped);
  const root = tentaclePathPoint(0);
  const rise = pose.rise;

  if (rise < 1) {
    centre.x = root.x + (centre.x - root.x) * rise;
    centre.y = root.y + (centre.y - root.y) * rise;
    centre.z = root.z + (centre.z - root.z) * rise;
  }

  const swing = tentacleOffset(pose.motion, clamped, simTime, pose.gripStrength);
  centre.x += swing.x * rise;
  centre.y += swing.y * rise;
  centre.z += swing.z * rise;

  if (pose.gripStrength <= 0) return centre;

  const wrapped = pose.shipGrip
    ? krakenTentacleWrapPoint(pose.shipGrip, clamped)
    : pose.rigGrip
      ? krakenRigWrapPoint(pose.rigGrip, clamped)
      : null;
  if (!wrapped) return centre;

  const rootBlend = clamp01(clamped / 0.16);
  const smoothRoot = rootBlend * rootBlend * (3 - 2 * rootBlend);
  const bind = pose.gripStrength * smoothRoot;
  centre.x += (wrapped.x - centre.x) * bind;
  centre.y += (wrapped.y - centre.y) * bind;
  centre.z += (wrapped.z - centre.z) * bind;
  /*
   * Muscle over the hold. A full bind snapped the limb rigidly onto its wrap and the
   * assault read as six static columns — a quarter of the free writhe survives on top,
   * so a gripping limb strains and breathes instead of standing dead. The tip of a
   * hooked limb sways a few metres with it, which reads as grip under load.
   */
  centre.x += swing.x * 0.25 * bind;
  centre.y += swing.y * 0.25 * bind;
  centre.z += swing.z * 0.25 * bind;
  return centre;
}
